import React, { useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaDownload, FaShippingFast, FaWhatsapp } from 'react-icons/fa';
import { format } from 'date-fns';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import  logo from '../assets/pgm-logo.png'

const getMonthCode = (date) => {
  const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  return months[date.getMonth()];
};

const getCurrentYearCode = (dateValue) => {
  const date = dateValue ? new Date(dateValue) : new Date();
  // Return last 2 digits of current year (e.g., 26 for 2026)
  return String(date.getFullYear()).slice(-2);
};

const formatInvoiceNumber = (order) => {
  if (!order) return '';

  const createdAtDate = order.createdAt ? new Date(order.createdAt) : new Date();
  // Use actual calendar month from order date
  const monthCode = getMonthCode(createdAtDate);
  // Use actual calendar year
  const yearCode = getCurrentYearCode(createdAtDate);

  const razorpayId = String(order.razorpay_order_id || '');

  if (order.invoice_number) {
    const padded = String(order.invoice_number).padStart(5, '0');
    return `${monthCode}${yearCode}-${padded}`;
  }

  const digits = razorpayId.replace(/\D/g, '');
  const padded = digits ? digits.slice(-5).padStart(5, '0') : '00000';
  return `${monthCode}${yearCode}-${padded}`;
};

const InvoicePreviewModal = ({ isOpen, onClose, invoiceData, onDownload, invoiceRef  }) => {
  const shippingRef = useRef();
  
  if (!isOpen || !invoiceData) return null;
  const { other_info: { shipping, items, productInfo }, razorpay_order_id, createdAt, status } = invoiceData;
  const invoiceNumber = formatInvoiceNumber(invoiceData);
  const itemsArray = items && Array.isArray(items) ? items : productInfo || [];

  const invoiceTotals = itemsArray.reduce(
    (acc, item) => {
      const itemPrice = Number(item.price) || 0;
      const itemQty = Number(item.quantity) || 0;
      const itemGstPercent = Number(item.gst ?? item.gst_percentage ?? item.gstPercent) || 0;
      const itemSubtotal = itemPrice * itemQty;
      const itemGstAmount = Number(((itemSubtotal * itemGstPercent) / 100).toFixed(2));

      acc.subtotal += itemSubtotal;
      acc.totalGST += itemGstAmount;
      acc.totalPoints += (Number(item.point) || 0) * itemQty;
      return acc;
    },
    { subtotal: 0, totalGST: 0, totalPoints: 0 }
  );

  const shippingAmount = 0;
  const grandTotal = Number((invoiceTotals.subtotal + invoiceTotals.totalGST + shippingAmount).toFixed(2));

  const openWhatsAppLink = (appUrl, webUrl) => {
    const fallbackTimer = setTimeout(() => {
      window.location.href = webUrl;
    }, 1500);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        clearTimeout(fallbackTimer);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange, { once: true });
    window.location.href = appUrl;
  };

  // Function to share the full invoice PDF on WhatsApp
  const handleShareOnWhatsApp = async () => {
    try {
      const phoneNumber = shipping.phone || shipping.phone_number;
      if (!phoneNumber) {
        alert('Phone number not available for sharing');
        return;
      }

      // Format phone number for WhatsApp
      let formattedPhone = phoneNumber.replace(/\D/g, '');
      if (!formattedPhone.startsWith('91')) {
        formattedPhone = '91' + formattedPhone;
      }

      const element = invoiceRef.current;
      if (!element) {
        throw new Error('Invoice reference not available');
      }

      const originalStyles = {
        width: element.style.width,
        maxWidth: element.style.maxWidth,
        minWidth: element.style.minWidth,
        position: element.style.position,
        overflow: element.style.overflow,
        maxHeight: element.style.maxHeight,
      };

      element.style.width = '1200px';
      element.style.maxWidth = '1200px';
      element.style.minWidth = '1200px';
      element.style.position = 'relative';
      element.style.overflow = 'visible';
      element.style.maxHeight = 'none';

      await new Promise((resolve) => setTimeout(resolve, 300));

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
        scrollX: 0,
        scrollY: 0,
      });

      Object.assign(element.style, originalStyles);

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const pxPerMm = canvas.width / pdfWidth;
      const pageHeightPx = Math.floor(pdfHeight * pxPerMm);

      let remainingHeight = canvas.height;
      let position = 0;
      let pageIndex = 0;

      while (remainingHeight > 0) {
        const sliceHeight = Math.min(pageHeightPx, remainingHeight);
        const pageCanvas = document.createElement('canvas');
        pageCanvas.width = canvas.width;
        pageCanvas.height = sliceHeight;
        const ctx = pageCanvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        ctx.drawImage(canvas, 0, position, canvas.width, sliceHeight, 0, 0, canvas.width, sliceHeight);

        const imgData = pageCanvas.toDataURL('image/png');
        const imgHeightMm = sliceHeight / pxPerMm;

        if (pageIndex > 0) pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, imgHeightMm);

        position += sliceHeight;
        remainingHeight -= sliceHeight;
        pageIndex += 1;
      }

      const pdfBlob = pdf.output('blob');
      const pdfFile = new File([pdfBlob], `invoice-${invoiceNumber || razorpay_order_id}.pdf`, { type: 'application/pdf' });

      if (navigator.share && navigator.canShare && navigator.canShare({ files: [pdfFile] })) {
        await navigator.share({
          files: [pdfFile],
          title: `Invoice - ${invoiceNumber || razorpay_order_id}`,
          text: `Invoice for Order ID: ${razorpay_order_id}`,
        });
        return;
      }

      const pdfURL = URL.createObjectURL(pdfBlob);
      const downloadLink = document.createElement('a');
      downloadLink.href = pdfURL;
      downloadLink.download = `invoice-${invoiceNumber || razorpay_order_id}.pdf`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(pdfURL);

      const message = `Hello ${shipping.name || ''},\n\nYour full invoice for Order ID: ${razorpay_order_id} has been generated.\n\nPlease attach the downloaded PDF invoice and send it via WhatsApp.\n\nThank you!`;
      const whatsappAppURL = `whatsapp://send?phone=${formattedPhone}&text=${encodeURIComponent(message)}`;
      const whatsappWebURL = `https://wa.me/${formattedPhone}?text=${encodeURIComponent(message)}`;
      openWhatsAppLink(whatsappAppURL, whatsappWebURL);
    } catch (error) {
      console.error('Error sharing on WhatsApp:', error);
      alert('Failed to share on WhatsApp. Please try again.');
    }
  };

  // Function to download only shipping details
  const handleDownloadShippingDetails = async () => {
    try {
      // Create a temporary container with better styling for PDF
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'fixed';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '0';
      tempContainer.style.background = 'white';
      tempContainer.style.padding = '40px';
      tempContainer.style.width = '800px';
      tempContainer.style.height = 'auto';
      tempContainer.style.fontFamily = 'Arial, sans-serif';
      tempContainer.style.fontSize = '14px';
      tempContainer.style.lineHeight = '1.6';
      tempContainer.style.color = '#333';
      tempContainer.style.zIndex = '-1';
      
      // Convert logo to base64 for embedding in PDF
      const getBase64Logo = () => {
        return new Promise((resolve) => {
          const img = new Image();
          img.onload = () => {
            try {
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');
              canvas.width = 120;
              canvas.height = 80;
              ctx.drawImage(img, 0, 0, 120, 80);
              resolve(canvas.toDataURL('image/png'));
            } catch (e) {
              console.error('Logo conversion error:', e);
              resolve('');
            }
          };
          img.onerror = () => {
            console.error('Logo loading error');
            resolve('');
          };
          // Try to load logo from the imported path
          img.src = logo;
        });
      };

      const logoBase64 = await getBase64Logo();
      
      tempContainer.innerHTML = `
        <div style="margin-bottom: 40px;">
          <div style="display: flex; align-items: center; justify-content: flex-start; margin-bottom: 30px; gap: 20px;">
            ${logoBase64 ? `<img src="${logoBase64}" alt="PGM Logo" style="height: 120px; width: 120px; object-fit: contain;" />` : '<div style="width: 120px; height: 80px; background: #f0f0f0; display: flex; align-items: center; justify-content: center; color: #888; font-size: 12px;">Logo</div>'}
            <div>
              <h1 style="color: #1f2937; font-size: 32px; margin: 0 0 10px 0; font-weight: bold;">Shipping Details</h1>
              <p style="color: #6b7280; font-size: 18px; margin: 0 0 5px 0;">Order ID: ${razorpay_order_id}</p>
              <p style="color: #6b7280; font-size: 16px; margin: 0;">Date: ${format(new Date(createdAt), 'PPP')}</p>
            </div>
          </div>
        </div>
        
        <div style="border-top: 3px solid #e5e7eb; border-bottom: 3px solid #e5e7eb; padding: 30px 0; margin: 30px 0;">
          <div style="display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 40px; align-items: flex-start;">
            <div>
              <h3 style="font-weight: bold; color: #374151; margin-bottom: 20px; font-size: 20px; border-bottom: 1px solid #e5e7eb; padding-bottom: 10px;">From:</h3>
              <div style="color: #4b5563; line-height: 1.8; font-size: 16px;">
                <p style="font-weight: bold; margin-bottom: 8px; font-size: 18px;">PGM Pvt. Ltd.</p>
                <p style="margin-bottom: 8px;">Tahsil road lodhipur</p>
                <p style="margin-bottom: 8px;">Zamania Kasba, Ghazipur</p>
                <p style="margin-bottom: 8px;">Uttar Pradesh - 232329</p>
                <p style="margin-bottom: 8px;"><strong>Phone:</strong> +91 8009842563</p>
                <p style="margin-bottom: 8px;"><strong>Email:</strong> inquairypgmproduct@gmail.com</p>
                <p style="margin-bottom: 8px;"><strong>GST:</strong> U46909UP2023OPC184835</p>
              </div>
            </div>
            <div>
              <h3 style="font-weight: bold; color: #374151; margin-bottom: 20px; font-size: 20px; border-bottom: 1px solid #e5e7eb; padding-bottom: 10px;">Shipping To:</h3>
              <div style="color: #4b5563; line-height: 1.8; font-size: 16px;">
                <p style="font-weight: bold; margin-bottom: 8px; font-size: 18px;">${shipping.name}</p>
                <p style="margin-bottom: 8px;"><strong>Phone:</strong> ${shipping.phone || shipping.phone_number}</p>
                <p style="margin-bottom: 8px;"><strong>Email:</strong> ${shipping.email}</p>
                <p style="margin-bottom: 8px;">${shipping.street_address}</p>
                ${shipping.additional_address_line ? `<p style="margin-bottom: 8px;">${shipping.additional_address_line}</p>` : ''}
                <p style="margin-bottom: 8px;">${shipping.city}, ${shipping.state} ${shipping.postal_code}</p>
                <p style="margin-bottom: 8px;">${shipping.country}</p>
              </div>
            </div>
          </div>
        </div>
        
        <div style="margin-top: 40px; text-align: center; color: #6b7280; font-size: 14px; border-top: 1px solid #e5e7eb; padding-top: 20px;">
          <p style="margin-bottom: 5px;"><strong>Shipping Details - Generated on ${format(new Date(), 'PPP')}</strong></p>
          <p style="margin: 0;">For any questions, please contact inquairypgmproduct@gmail.com</p>
        </div>
      `;
      
      document.body.appendChild(tempContainer);
      
      // Wait for DOM to render and images to load
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Capture the container with enhanced options
      const canvas = await html2canvas(tempContainer, { 
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        width: 800,
        height: tempContainer.scrollHeight,
        logging: false,
        removeContainer: false,
        foreignObjectRendering: false
      });
      
      // Remove temporary container
      document.body.removeChild(tempContainer);
      
      // Create PDF
      const imgData = canvas.toDataURL('image/png', 1.0);
      const pdf = new jsPDF('p', 'mm', 'a4');
      
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      
      const imgProps = pdf.getImageProperties(imgData);
      const imgWidth = pageWidth - 20; // 10mm margin on each side
      const imgHeight = (imgProps.height * imgWidth) / imgProps.width;
      
      // Center the content on the page
      const xPos = 10; // 10mm margin
      const yPos = 10; // 10mm margin
      
      // Add the image to PDF
      if (imgHeight > pageHeight - 20) {
        // If content is too tall, scale it down
        const scaledHeight = pageHeight - 20;
        const scaledWidth = (imgWidth * scaledHeight) / imgHeight;
        pdf.addImage(imgData, 'PNG', (pageWidth - scaledWidth) / 2, yPos, scaledWidth, scaledHeight);
      } else {
        pdf.addImage(imgData, 'PNG', xPos, yPos, imgWidth, imgHeight);
      }
      
      pdf.save(`shipping-details-${invoiceNumber || razorpay_order_id || 'pgm'}.pdf`);
      
    } catch (error) {
      console.error('Error generating shipping details PDF:', error);
      alert('Failed to generate shipping details PDF. Please try again.');
    }
  };

  return (
    <AnimatePresence>
      <motion.div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div 
          className="bg-white rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-4 flex justify-between items-center">
            <h2 className="text-xl font-semibold text-white">Invoice Preview</h2>
            <div className="flex gap-2">
                <button
                onClick={handleShareOnWhatsApp}
                className="flex items-center gap-2 px-3 py-2 bg-green-500 hover:bg-green-600 rounded-lg text-white transition-colors text-sm font-medium"
                title="Share on WhatsApp"
              >
                <FaWhatsapp className="w-4 h-4" />
                <span className="hidden sm:inline">WhatsApp</span>
              </button>
              <button
                onClick={handleDownloadShippingDetails}
                className="flex items-center gap-2 px-3 py-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white transition-colors text-sm font-medium"
                title="Download Shipping Details Only"
              >
                <FaShippingFast className="w-4 h-4" />
                <span className="hidden sm:inline">Shipping Details</span>
              </button>
              <button
                onClick={onDownload}
                className="flex items-center gap-2 px-3 py-2 bg-purple-500 hover:bg-purple-600 rounded-lg text-white transition-colors text-sm font-medium"
                title="Download Full Invoice"
              >
                <FaDownload className="w-4 h-4" />
                <span className="hidden sm:inline">Full Invoice</span>
              </button>
              <button
                onClick={onClose}
                className="p-2 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-colors"
                title="Close"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Invoice Content */}
          <div ref={invoiceRef} className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
            <div className="bg-white rounded-lg shadow-inner border p-8 space-y-6">
              {/* Invoice Header */}
              <div className="flex justify-between items-start">
                <div className=' flex flex-row items-start justify-start space-x-4'>
                    <div className="flex items-center space-x-4">
                <img src={logo} alt="PGM Logo" className="h-20 w-auto" />
                </div>
                    <div>
                  <h1 className="text-2xl font-bold text-gray-900">INVOICE</h1>
                  <p className="text-gray-600 mt-1">Invoice No: Invoice#{invoiceNumber}</p>
                  <p className="text-gray-600 mt-1">Order ID: {razorpay_order_id}</p>
                  <p className="text-sm text-gray-500">
                    Date: {format(new Date(createdAt), 'PPP')}
                  </p>
                </div>
                </div>
                <div className="text-right">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium
                    ${status === 'DELIVERED' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}
                  >
                    {status}
                  </span>
                </div>
              </div>

              {/* Shipping Details */}
              <div ref={shippingRef} className="grid grid-cols-1 lg:grid-cols-2 gap-8 border-t border-b py-6">
                <div>
                  <h3 className="font-semibold text-gray-800 mb-3">From:</h3>
                  <div className="space-y-1 text-gray-600">
                    <p className="font-medium">PGM Pvt. Ltd.</p>
                    <p>Tahsil road lodhipur</p>
                    <p>Zamania Kasba, Ghazipur, Uttar Pradesh  - 232329</p>
                    <p>Phone: +91 8009842563</p>
                    <p>Email: inquairypgmproduct@gmail.com</p>
                    <p>GST: U46909UP2023OPC184835</p>
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-800 mb-3">Shipping To:</h3>
                  <div className="space-y-1 text-gray-600">
                    <p className="font-medium">{shipping.name}</p>
                    <p>Phone: {shipping.phone || shipping.phone_number}</p>
                    <p>Email: {shipping.email}</p>
                    <p>{shipping.street_address}</p>
                    {shipping.additional_address_line && <p>{shipping.additional_address_line}</p>}
                    <p>{shipping.city}, {shipping.state} {shipping.postal_code}</p>
                    <p>{shipping.country}</p>
                  </div>
                </div>
              </div>

              {/* Products Table */}
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="py-3 text-left text-gray-600">Product</th>
                    <th className="py-3 text-left text-gray-600">HSN Code</th>
                    <th className="py-3 text-center text-gray-600">Quantity</th>
                    <th className="py-3 text-right text-gray-600">Price</th>
                    <th className="py-3 text-right text-gray-600">GST %</th>
                    <th className="py-3 text-right text-gray-600">GST Amount</th>
                    <th className="py-3 text-right text-gray-600">Points</th>
                    <th className="py-3 text-right text-gray-600">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {itemsArray.map((product, index) => {
                    const quantity = Number(product.quantity) || 0;
                    const price = Number(product.price) || 0;
                    const hsnCode = product.hsn || product.hsn_code || product.hsnCode || 'N/A';
                    const gstPercentage = Number(product.gst ?? product.gst_percentage ?? product.gstPercent) || 0;
                    const itemSubtotal = quantity * price;
                    const itemGstAmount = Number(((itemSubtotal * gstPercentage) / 100).toFixed(2));

                    return (
                    <tr key={product.id || `${product.name || 'product'}-${index}`} className="border-b">
                      <td className="py-4">
                        <div>
                          <p className="font-medium text-gray-900">{product.name}</p>
                          <p className="text-sm text-gray-500">{product.category}</p>
                        </div>
                      </td>
                      <td className="py-4 text-left text-gray-600">{hsnCode}</td>
                      <td className="py-4 text-center text-gray-600">{quantity}</td>
                      <td className="py-4 text-right text-gray-600">₹{price} × {quantity}</td>
                      <td className="py-4 text-right text-gray-600">{gstPercentage}%</td>
                      <td className="py-4 text-right text-gray-600">₹{itemGstAmount.toFixed(2)}</td>
                      <td className="py-4 text-right text-gray-600">{product.point} × {quantity}</td>
                      <td className="py-4 text-right text-gray-900 font-medium">
                        ₹{(itemSubtotal + itemGstAmount).toFixed(2)}
                      </td>
                    </tr>
                  )})}
                </tbody>
              </table>

         
                      <div className="flex justify-end">
                      <div className="w-72 space-y-3">
                        <>
                        <div className="flex justify-between text-gray-600">
                          <span>Subtotal:</span>
                          <span>₹{invoiceTotals.subtotal.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-gray-600">
                          <span>GST:</span>
                          <span>₹{invoiceTotals.totalGST.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-gray-600">
                          <span>Shipping:</span>
                          <span>₹{shippingAmount.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between font-bold text-gray-900 text-lg border-t pt-3">
                          <span>Total:</span>
                          <span>₹{grandTotal.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm text-gray-500">
                          <span>Points Earned:</span>
                          <span>{invoiceTotals.totalPoints} Points</span>
                        </div>
                        </>
                      </div>
                      </div>

                      {/* Footer */}
              <div className="border-t pt-6 text-center text-gray-500 text-sm">
                <p>Thank you for your purchase!</p>
                <p>For any questions, please contact inquairypgmproduct@gmail.com</p>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default InvoicePreviewModal;